/* eslint-disable react/prop-types */
import React, { ReactElement, useMemo, useState } from "react";
import { Transaction } from "../../models/Transaction";
import { MantineReactTable, MRT_ColumnDef, MRT_SortingState, useMantineReactTable } from "mantine-react-table";
import { formatAddress } from "../../utils/formatter";
import { formatDate, useAvailableTime } from "../../utils/formatTime";
import styles from "./AddressDetailsTabs.module.scss";
import { useParams } from "react-router-dom";
import CopyTextComponent from "../copyText/CopyText.component";
import { useQuery } from "@tanstack/react-query";
import { QueryKey, TransactionType } from "../../models/enums";
import { TransactionsResponse } from "../../models/Response";
import { IndexerService } from "../../services/indexer.service";
import { Text } from "@mantine/core";
import TableFilterDropdown, { OptionValue } from "../tableFilterDropdown/TableFilterDropdown";
import { getTransactionValue } from "../transactionDetailsTable/TransactionDetailsTable.component";
import { getTransactionTypeLabel, TxKeys } from "../../pages/transactionDetails/TransactionConfig";
import { useStore } from "@tanstack/react-store";
import { IStore, store } from "../../services/store.service";

export const transactionTypes: OptionValue[] = [
  { value: "all", label: "All types" },
  { value: TransactionType.EXPORT_TX, label: "Export" },
  { value: TransactionType.IMPORT_TX, label: "Import" },
  { value: TransactionType.ADD_VALIDATOR_TX, label: "Add validator" },
  { value: TransactionType.ADD_DELEGATOR_TX, label: "Add delegator" },
  { value: TransactionType.ADD_PERMISSIONLESS_VALIDATOR_TX, label: "Add permissionless validator" },
  { value: TransactionType.ADD_PERMISSIONLESS_DELEGATOR_TX, label: "Add permissionless delegator" },
  { value: TransactionType.BASE_TX, label: "Native transfer" },
];

const initialPagination = {
  pageIndex: 0,
  pageSize: 10, //customize the default page size
};
export default function TransactionTab({
                                         active,
                                       }: {
  active: boolean;
}): ReactElement {
  const timeZone = useStore<IStore, IStore["timeZone"]>(store, (state) => state.timeZone).selected;
  const { address } = useParams();

  //store pagination state in your own state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10, //customize the default page size
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [selectedType, setSelectedType] = useState<TransactionType | null>(
    null,
  );

  const { data: transactionsData } = useQuery({
    queryKey: [
      QueryKey.transactionList,
      address,
      pagination.pageIndex,
      pagination.pageSize,
      selectedType,
      sorting,
    ],
    queryFn: (): Promise<TransactionsResponse> => {
      const timestampSorting = sorting.find((item) => item.id === "Timestamp");
      console.log("sorting", sorting);
      return IndexerService.getTransactionsPerAddress(
        address!,
        null,
        null,
        pagination.pageIndex * pagination.pageSize,
        pagination.pageSize,
        timestampSorting ? (timestampSorting.desc ? "desc" : "asc") : undefined,
        selectedType,
      );
    },
    enabled: !!address && active,
    staleTime: 10000,
  });

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<Transaction>[]>(
    () => [
      {
        accessorKey: "txID",
        header: "Id",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
        enableColumnFilter: false,
        size: 175,
        Cell: ({ cell }) => (
          <CopyTextComponent
            primaryColor={false}
            text={formatAddress(cell.getValue<string>())}
            value={cell.getValue<string>()}
            link={`/tx/${cell.getValue<string>()}`}
            size={"14px"}
            weight={"500"}
          ></CopyTextComponent>
        ),
      },
      {
        accessorFn: (tx) => {
          return (tx.type);
        },
        header: "Type",
        Header: () => (
          <>
            <Text fw={500}>Type</Text>
            <TableFilterDropdown
              values={transactionTypes}
              setSelectedValue={(item) => {
                setSelectedType(item ? (item as TransactionType) : null);
                setPagination(initialPagination);
              }}
              selectedValue={selectedType}
            />
          </>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
        enableColumnFilter: true,
        filterVariant: "select",

        size: 225,
        Cell: ({ cell }) => (
          <span style={{ textTransform: "capitalize" }}>
            {getTransactionTypeLabel(cell.getValue<string>() as TransactionType)}
          </span>
        ),
      },
      {
        id: "amount", //normal accessorKey
        header: "Amount",
        // size: 100,
        mantineTableHeadCellProps: {
          align: "right",
        },
        mantineTableBodyCellProps: {
          align: "right",
        },
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <>{getTransactionValue(row.original, TxKeys.AMOUNT)}</>
        ),
      },
      {
        accessorFn: (tx) => {
          return useAvailableTime(tx);
        },
        header: "Timestamp",
        size: 250,
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <>
            {cell.getValue<string>() === null ? (
              <span> No date provided</span>
            ) : (
              <span>
                {
                  formatDate(new Date(cell.getValue<string>()), timeZone)
                    .formattedDateTime
                }
                &nbsp;
                <span className={styles.unitLabel}>
                  {formatDate(new Date(cell.getValue<string>()), timeZone).utcOffset}
                </span>
              </span>
            )}
          </>
        ),
      },
    ],
    [transactionsData?.data, timeZone],
  );

  const table = useMantineReactTable({
    columns: transactionsData ? columns : [],
    data: transactionsData?.data.transactions || [], //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineTableProps: {
      style: {
        tableLayout: "fixed",
        fontWeight: 500,
        fontSize: "var(--module-size-14)",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: 500,
        fontSize: 15,
      },
    },
    state: {
      isLoading: !transactionsData,
      pagination,
      sorting,
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: "none",
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableFilters: false,
    initialState: {
      density: "xs",
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: transactionsData?.data.count,
    onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    onSortingChange: setSorting,
    mantinePaginationProps: {
      showRowsPerPage: false,
      withEdges: false,
    },
  });

  return <MantineReactTable table={table} />;
}
